import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnInit,
} from '@angular/core';
import { Customer } from '@usf/customer-types';
import { NotificationGroup, DeliveryMethod } from '@usf/ngrx-alerts';
import { PlatformService, UserState } from '@panamax/app-state';
import { PendingNotificationPreferencesService } from '../../../services/pending-notfication-preferences.service';

@Component({
  selector: 'app-alert-group-card',
  templateUrl: './alert-group-card.component.html',
  styleUrls: ['./alert-group-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertGroupCardComponent implements OnInit {
  @Input() alertGroup!: NotificationGroup;
  @Input() availableCustomers!: Customer[];
  @Input() user: UserState;
  @Input() isInternalUser: boolean;
  @Output() saveNotificationPreferencesEmitter = new EventEmitter<any>();
  @Output() manageCustomersEmitter = new EventEmitter<void>();
  alertGroupDescription: string;

  constructor(
    public readonly platformService: PlatformService,
    private readonly pendingNotificationPreferencesService: PendingNotificationPreferencesService,
  ) {}

  ngOnInit(): void {
    // External users get a different description for GENERAL_DELIVERY that hides Fulfillment Exception
    if (this.alertGroup?.name === 'GENERAL_DELIVERY' && !this.isInternalUser) {
      this.alertGroupDescription =
        'i18n.notifications.preferences.' + this.alertGroup?.name + '_DESC_EXT';
    } else {
      this.alertGroupDescription =
        'i18n.notifications.preferences.' + this.alertGroup?.name + '_DESC';
    }
  }

  /**
   * Toggles notification delivery methods for the alert group
   * @param deliveryMethods Array of delivery methods to toggle
   */
  toggleNotificationType(deliveryMethods: DeliveryMethod[]): void {
    this.updateNotificationTypes(deliveryMethods);
  }

  /**
   * Handles toggling all customers for the alert group
   * @param event Event containing the toggle state
   */
  toggleAllCustomers(event: any): void {
    // If enabling notifications and no delivery methods are set, set default method
    if (
      event.detail.checked &&
      this.alertGroup?.enabledDeliveryMethods?.length === 0
    ) {
      if (this.user?.email?.length) {
        this.updateNotificationTypes(['EMAIL']);
      } else if (this.alertGroup?.availableDeliveryMethods?.includes('PUSH')) {
        this.updateNotificationTypes(['PUSH']);
      }
    }

    // Handle disabling all notifications
    if (!event.detail.checked) {
      this.alertGroup = {
        ...this.alertGroup,
        enabledDeliveryMethods: [],
        enabledCustomers: [],
      };
    } else {
      // Enable notifications for all available customers
      this.alertGroup = {
        ...this.alertGroup,
        enabledCustomers: this.availableCustomers.map(customer => ({
          customerNumber: customer.customerNumber.toString(),
          divisionNumber: customer.divisionNumber.toString(),
        })),
      };
    }
    this.saveNotificationPreferences(event.detail.checked);
  }

  /**
   * Emits the current notification preferences to be saved
   * @param toggleAllValue Optional boolean indicating if all notifications were toggled
   */
  saveNotificationPreferences(toggleAllValue?: boolean) {
    this.saveNotificationPreferencesEmitter.emit({
      group: this.alertGroup,
      toggleAllValue: toggleAllValue,
    });
  }

  /**
   * Triggers the manage customers action
   */
  manageCustomers(): void {
    this.manageCustomersEmitter.emit();
  }

  /**
   * Updates the enabled delivery methods for the alert group
   * @param deliveryMethods Array of delivery methods to update
   */
  private updateNotificationTypes(deliveryMethods: DeliveryMethod[]) {
    let newEnabledMethods = [...this.alertGroup.enabledDeliveryMethods];
    const method = deliveryMethods[0];
    const methodExists = newEnabledMethods.includes(method);
    if (methodExists) {
      newEnabledMethods = newEnabledMethods.filter(m => m !== method);
    } else {
      newEnabledMethods.push(method);
    }

    this.pendingNotificationPreferencesService.addPendingPreference(
      this.alertGroup.id,
      {
        email: newEnabledMethods.includes('EMAIL'),
        push: newEnabledMethods.includes('PUSH'),
      },
    );

    // Update the alert group with new delivery methods and customers
    this.alertGroup = {
      ...this.alertGroup,
      enabledDeliveryMethods: newEnabledMethods,
      enabledCustomers: this.alertGroup.enabledCustomers,
    };

    // Only save if there are enabled delivery methods or if there are enabled customers
    if (
      newEnabledMethods.length > 0 ||
      this.alertGroup.enabledCustomers.length > 0
    ) {
      this.saveNotificationPreferences();
    }
  }
}
